import { Launch } from "@mui/icons-material";
import { Button, Divider, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { WebReport2InsomniaRiskData } from "..";
import { NumberTitle, RiskLabel, RiskProps, WrappedSection } from "../commons";
import { InsomniaRiskLevelChart } from "../DiseaseRiskLevelChart";

export interface InsomniaRiskProps
  extends RiskProps,
    WebReport2InsomniaRiskData {
  grade: string;
  sleep_length_result: string;
  sleep_quality_result: string;
}
export function InsomniaRisk({
  grade,
  type,
  title,
  summary,
  description_lines,
  AIS_score: score,
  sleep_length_result,
  sleep_quality_result,
}: InsomniaRiskProps) {
  const getResultColor = (type: string) =>
    ({ 注意: "#FF910B", 警戒: "#FF3A30" })[type] ?? "text.primary";

  /**
   * 疾患リスクレポート表示リンク用
   */
  const location = useLocation();
  const openDiseaseRiskReport = useCallback(() => {
    const pathname = location.pathname;
    window.open(`${pathname}${pathname.endsWith("/") ? "" : "/"}disease_risk`);
  }, [location]);
  return (
    <>
      <Stack sx={{ mx: 6, gap: 4 }}>
        <NumberTitle number={2}>不眠症のリスク</NumberTitle>
        <RiskLabel type={type} title={title} summary={summary} />
        <Typography variant="body1" textAlign="justify" color="text.primary">
          {description_lines}
        </Typography>
      </Stack>
      <Divider
        sx={{
          mt: 12,
          mb: 6,
          mx: 6,
        }}
      />
      <Stack sx={{ mx: 6, gap: 6 }}>
        <Typography variant="subtitle1">
          問診の結果<sup>※</sup>
        </Typography>

        <Stack gap={4}>
          <Stack>
            <InsomniaRiskLevelChart insomniaScore={score} />
          </Stack>
        </Stack>

        <Stack alignItems="center">
          <Stack direction="row" gap={2}>
            <Typography
              variant="caption"
              sx={{
                "&::before": {
                  content: "'■'",
                  marginRight: 1,
                  color: "#7FAACF",
                },
              }}
            >
              低リスク
            </Typography>
            <Typography
              variant="caption"
              sx={{
                "&::before": {
                  content: "'■'",
                  marginRight: 1,
                  color: "#FF9C97",
                },
              }}
            >
              疑いあり
            </Typography>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          ※問診の根拠：アテネ不眠尺度（臨床睡眠検査マニュアル│日本睡眠学会）
        </Typography>
        <Divider sx={{ border: "none", borderTop: "2px dotted #E0E0E0" }} />
        <Stack>
          <Typography variant="subtitle1">
            デバイス測定による睡眠の状態
          </Typography>

          <Stack>
            <table style={{ width: "100%" }}>
              <tbody>
                <tr>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        "&::before": {
                          content: "'・'",
                          color: "text.secondary",
                          mr: 2,
                        },
                      }}
                    >
                      睡眠の長さ
                    </Typography>
                  </td>
                  <td width="100%">
                    <Divider
                      sx={{
                        mx: 2,
                        border: "none",
                        borderTop: "2px dashed #E0E0E0",
                        "&:before": {
                          content: "''",
                          position: "absolute",
                          left: 0,
                          top: "50%",
                          width: "100%",
                          height: "1px",
                        },
                      }}
                    />
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Typography
                      fontWeight={500}
                      fontSize="16px"
                      color={getResultColor(sleep_length_result)}
                    >
                      {sleep_length_result}
                    </Typography>
                  </td>
                </tr>
              </tbody>
            </table>

            <table style={{ width: "100%" }}>
              <tbody>
                <tr>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        "&::before": {
                          content: "'・'",
                          color: "text.secondary",
                          mr: 2,
                        },
                      }}
                    >
                      睡眠の質
                    </Typography>
                  </td>
                  <td width="100%">
                    <Divider
                      sx={{
                        mx: 2,
                        border: "none",
                        borderTop: "2px dashed #E0E0E0",
                        "&:before": {
                          content: "''",
                          position: "absolute",
                          left: 0,
                          top: "50%",
                          width: "100%",
                          height: "1px",
                        },
                      }}
                    />
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Typography
                      fontWeight={500}
                      fontSize="16px"
                      color={getResultColor(sleep_quality_result)}
                    >
                      {sleep_quality_result}
                    </Typography>
                  </td>
                </tr>
              </tbody>
            </table>
          </Stack>
        </Stack>
        <Divider />
        {grade[0] !== "E" && (
          <WrappedSection
            title="疾患リスクレポート（医療機関用）について"
            color="#4D89BD"
          >
            <Typography
              variant="body2"
              sx={{ textAlign: "justify", color: "text.secondary" }}
            >
              今回の測定結果について医療機関へ共有する場合は
              <Button
                variant="text"
                endIcon={<Launch />}
                sx={{
                  color: "text.secondary",
                  textDecoration: "underline",
                }}
                onClick={() => {
                  openDiseaseRiskReport();
                }}
              >
                こちら
              </Button>
              をご利用ください。
            </Typography>
          </WrappedSection>
        )}
      </Stack>
    </>
  );
}
